import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queuesApi, agentsApi, clientsApi } from '../../../api/client';
import { Button, Badge } from '../../ui/Modal';
import QueueEditModal from './QueueEditModal';
import HelpButton from '../../help/HelpButton';

export default function QueuesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | queue
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const { data: queues = [], isLoading, refetch } = useQuery({
    queryKey: ['queues'],
    queryFn: queuesApi.list,
  });

  // We need the agents list for the member picker in the edit modal.
  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => clientsApi.list(),
    staleTime: 60_000,
  });
  const clientNameById = useMemo(
    () => Object.fromEntries((clients || []).map((c) => [c.id, c.name])),
    [clients]
  );

  const { data: agents = [] } = useQuery({
    queryKey: ['agents', {}],
    queryFn: () => agentsApi.list(),
  });

  const delMut = useMutation({
    mutationFn: (id) => queuesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['queues'] }),
  });

  const onSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await queuesApi.sync();
      setSyncResult({ ok: true, tag: res.tag, summary: res.queues || {} });
    } catch (err) {
      setSyncResult({ ok: false, error: err?.detail || err?.message || 'sync_failed' });
    } finally {
      setSyncing(false);
      qc.invalidateQueries({ queryKey: ['queues'] });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Queues</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            ACD queues are pushed to LOU Issabel <code>/etc/asterisk/queues_custom.conf</code> on save.
            Portal-managed queues use the <strong>6xxxx</strong> range to avoid collisions with the existing Issabel-GUI queues.
          </p>
        </div>
        <div className="flex gap-2">
          <HelpButton moduleKey="admin_queues" />
          <Button onClick={onSync} loading={syncing}>↻ Re-sync all</Button>
          <Button variant="primary" onClick={() => setEditing('new')}>+ New queue</Button>
        </div>
      </div>

      {syncResult && (
        <div className={
          'mb-4 px-3 py-2 rounded-thin text-sm ' +
          (syncResult.ok
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-200'
            : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200')
        }>
          {syncResult.ok ? (
            <>✓ Sync OK ({syncResult.tag}) — {Object.keys(syncResult.summary).length} queue(s) pushed</>
          ) : (
            <>✗ Sync failed: {syncResult.error}</>
          )}
        </div>
      )}

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle text-left">
            <tr className="text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Tenant</th>
              <th className="px-3 py-2">Strategy</th>
              <th className="px-3 py-2">Members</th>
              <th className="px-3 py-2">Timeout</th>
              <th className="px-3 py-2">Wrap</th>
              <th className="px-3 py-2">MOH</th>
              <th className="px-3 py-2">Recording</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Synced</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-surface-muted">Loading…</td></tr>
            )}
            {!isLoading && queues.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center text-surface-muted">
                  No queues yet. Click <strong>+ New queue</strong> to create one.
                </td>
              </tr>
            )}
            {queues.map((q) => (
              <tr key={q.id} className="border-t border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle/40 dark:hover:bg-surface-dark-subtle/40">
                <td className="px-3 py-2 font-mono">{q.queue_id}</td>
                <td className="px-3 py-2">{q.name}</td>
                <td className="px-3 py-2 text-xs">
                  {q.client_id
                    ? <Badge tone="purple">{clientNameById[q.client_id] || 'client'}</Badge>
                    : <span className="text-surface-muted">Continuum</span>}
                </td>
                <td className="px-3 py-2"><Badge tone="blue">{q.strategy}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {q.members.length === 0 ? <span className="text-surface-muted">—</span> :
                      q.members.map((m) => <span key={m} className="font-mono text-xs">{m}</span>)
                        .reduce((acc, el, i) => acc.length === 0 ? [el] : [...acc, <span key={'s'+i} className="text-surface-muted">·</span>, el], [])
                    }
                  </div>
                </td>
                <td className="px-3 py-2 text-center">{q.timeout}s</td>
                <td className="px-3 py-2 text-center">{q.wrapup_time}s</td>
                <td className="px-3 py-2 text-xs">{q.music_class}</td>
                <td className="px-3 py-2">{q.recording_enabled ? <Badge tone="green">on</Badge> : <Badge>off</Badge>}</td>
                <td className="px-3 py-2">{q.enabled ? <Badge tone="green">Active</Badge> : <Badge tone="red">Disabled</Badge>}</td>
                <td className="px-3 py-2 text-xs">
                  {q.last_synced_at
                    ? <Badge tone="green">{new Date(q.last_synced_at).toLocaleTimeString()}</Badge>
                    : q.last_sync_error
                      ? <Badge tone="red" title={q.last_sync_error}>error</Badge>
                      : <Badge tone="amber">pending</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" onClick={() => setEditing(q)}>Edit</Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (confirm(`Delete queue ${q.queue_id} (${q.name})?\n\nThis will also remove it from LOU Issabel on next sync.`)) {
                          delMut.mutate(q.id);
                        }
                      }}
                    >Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-surface-muted dark:text-surface-dark-muted">
        {queues.length} queue{queues.length === 1 ? '' : 's'} shown. Click <strong>↻ Re-sync all</strong> after edits to push to LOU Issabel.
      </div>

      {editing && (
        <QueueEditModal
          queue={editing === 'new' ? null : editing}
          agents={agents}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['queues'] }); }}
        />
      )}
    </div>
  );
}
